import { parsePhoneNumber as parseLibPhoneNumber } from 'libphonenumber-js';
import type { SelectOption } from '../../components';

export const genderOptions: SelectOption[] = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
];

export const MANGLIK_LABELS: Record<string, string> = {
  manglik: 'Manglik',
  non_manglik: 'Non-Manglik',
  anshik_manglik: 'Anshik Manglik',
  not_sure: 'Not Sure',
};

export const manglikOptions: SelectOption[] = [
  { value: 'manglik', label: MANGLIK_LABELS.manglik },
  { value: 'non_manglik', label: MANGLIK_LABELS.non_manglik },
  { value: 'anshik_manglik', label: MANGLIK_LABELS.anshik_manglik },
  { value: 'not_sure', label: MANGLIK_LABELS.not_sure },
];

export const formatManglikLabel = (value?: string | null): string => {
  if (!value) return '—';
  if (MANGLIK_LABELS[value]) return MANGLIK_LABELS[value];
  return value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join('-');
};

export const horoscopeOptions: SelectOption[] = [
  { value: 'must', label: 'Must' },
  { value: 'not_necessary', label: 'Not Necessary' },
];

export const PHONE_COUNTRY_CODE_OPTIONS: SelectOption[] = [
  { value: '+91', label: '+91' },
  { value: '+1', label: '+1' },
  { value: '+44', label: '+44' },
  { value: '+971', label: '+971' },
  { value: '+61', label: '+61' },
  { value: '+65', label: '+65' },
  { value: '+60', label: '+60' },
  { value: '+49', label: '+49' },
  { value: '+33', label: '+33' },
  { value: '+81', label: '+81' },
  { value: '+86', label: '+86' },
  { value: '+966', label: '+966' },
  { value: '+974', label: '+974' },
  { value: '+968', label: '+968' },
  { value: '+965', label: '+965' },
];

export const DEFAULT_PHONE_COUNTRY_CODE = '+91';

export const buildFullPhoneNumber = (countryCode: string, localNumber: string): string => {
  const digits = localNumber.replace(/\D/g, '');
  if (!digits) return '';
  return `${countryCode}${digits}`;
};

export const parsePhoneNumber = (
  fullPhone?: string | null
): { countryCode: string; localNumber: string } => {
  const trimmed = fullPhone?.replace(/\s+/g, '').trim() ?? '';
  if (!trimmed) {
    return { countryCode: DEFAULT_PHONE_COUNTRY_CODE, localNumber: '' };
  }

  if (/^\+\d{1,4}$/.test(trimmed)) {
    return { countryCode: trimmed, localNumber: '' };
  }

  try {
    const parsed = parseLibPhoneNumber(trimmed);
    if (parsed) {
      return {
        countryCode: `+${parsed.countryCallingCode}`,
        localNumber: parsed.nationalNumber,
      };
    }
  } catch {
    // Fall back to prefix matching below.
  }

  const sortedOptions = [...PHONE_COUNTRY_CODE_OPTIONS].sort(
    (a, b) => b.value.length - a.value.length
  );
  const matched = sortedOptions.find((option) => trimmed.startsWith(option.value));
  if (matched) {
    return {
      countryCode: matched.value,
      localNumber: trimmed.slice(matched.value.length).replace(/\D/g, ''),
    };
  }

  return {
    countryCode: DEFAULT_PHONE_COUNTRY_CODE,
    localNumber: trimmed.replace(/\D/g, ''),
  };
};

export interface ProfilePhotoPayloadSource {
  mainPhoto: { id: number } | null;
  galleryPhotos: { id: number }[];
}

export const buildProfilePhotoPayload = (photoState: ProfilePhotoPayloadSource) => ({
  profilePictureId: photoState.mainPhoto?.id && photoState.mainPhoto.id > 0
    ? photoState.mainPhoto.id
    : null,
  otherPicturesIds: photoState.galleryPhotos
    .filter((photo): photo is { id: number } => Boolean(photo?.id && photo.id > 0))
    .map((photo) => photo.id),
});
